import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import type { UpdateAppointmentRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

// Forward-compatible: backend will add this field to UpdateAppointmentRequest soon.
// Once you re-generate OpenAPI, this type can be removed and the casts below can go away.
export type UpdateAppointmentRequestWithApproval = UpdateAppointmentRequest & {
  isApprovalRequired?: boolean | null;
};

export const useAppointmentUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation<
    unknown,
    any,
    UpdateAppointmentRequestWithApproval
  >({
    mutationFn: async (
      updateAppointmentRequest: UpdateAppointmentRequestWithApproval,
    ) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );

      const cleanedRequest: UpdateAppointmentRequestWithApproval = {
        ...updateAppointmentRequest,
        description:
          updateAppointmentRequest.description === ""
            ? null
            : (updateAppointmentRequest.description ?? null),
        onlineMeetingUrl:
          updateAppointmentRequest.onlineMeetingUrl === ""
            ? null
            : (updateAppointmentRequest.onlineMeetingUrl ?? null),
        bookingNotice:
          updateAppointmentRequest.bookingNotice === ""
            ? null
            : (updateAppointmentRequest.bookingNotice ?? null),
        cancellationNotice:
          updateAppointmentRequest.cancellationNotice === ""
            ? null
            : (updateAppointmentRequest.cancellationNotice ?? null),
        appointmentTypeId: updateAppointmentRequest.appointmentTypeId ?? null,
        startDate: updateAppointmentRequest.startDate ?? null,
        endDate: updateAppointmentRequest.endDate ?? null,
      };

      return service.apiAppointmentsPut({
        updateAppointmentRequest: cleanedRequest as unknown as UpdateAppointmentRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentSchedule],
      });

      toast({
        title: "Private lesson updated",
        description: "Private lesson has been updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update appointment";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isSuccess, isError };
};
